<template>
    <div
        class="search"
        :class="{
            [`theme-${layout.theme}`]: true,
        }"
    >
        <button class="ds-btn ds-btn--quartiary search-button" @click="activateSearch">
            <span>{{ $t('search.search') }}</span>
            <DSIcon code="st1website-fill/search" />
        </button>
        <Teleport v-if="modal.name === 'search'" :to="modal.modalTarget">
            <div class="ds-modal-content ds-modal-search lenis-ignore">
                <div class="head">
                    <slot v-if="$slots.head" name="head" />
                    <nuxt-link v-else class="logo" :to="$paths.getPath('landingPage')">
                        <object :data="logo.src" :alt="logo.alt" />
                    </nuxt-link>
                    <button class="close-btn" @click="modal.unsetNameAndClose">
                        <DSIcon code="st1website-line/close" size="xl" />
                    </button>
                </div>
                <div class="body">
                    <DSInput
                        v-model="searchPrompt"
                        :icon-after="searchPrompt ? 'delete-text' : 'search'"
                        :label="$t('search.label')"
                        @input="onSearchInput"
                        @click-after="clearSearchPrompt"
                        @blur="gtmPushSearchResults()"
                    />
                    <div v-if="searchPrompt.length > 0" class="search-results-header">
                        <div v-if="totalResults > 0" class="results-summary">
                            <div :aria-label="`${totalResults} ${$t('search.searchResultsAmount')}`">
                                <span class="result-data">{{ totalResults }}</span>
                                <span class="result-label">{{ $t('search.searchResultsAmount') }}</span>
                            </div>
                            <div>
                                <span class="result-label">{{ $t('search.searchResultsPages') }}</span>
                                <span class="result-data">{{ `${currentPage}/${pages}` }}</span>
                            </div>
                        </div>
                        <div v-if="totalResults > 0 || currentlySelectedFilter" class="results-options">
                            <button
                                class="ds-btn ds-btn--quartinary"
                                :class="{selected: !currentlySelectedFilter}"
                                @click="() => clearSelectedFilter()"
                            >
                                {{ $t('search.showAll') }}
                            </button>
                            <button
                                v-for="(filter, index) in filters"
                                :key="`filter-${filter}-${index}`"
                                class="ds-btn ds-btn--quartinary"
                                :class="{selected: currentlySelectedFilter === filter}"
                                @click="() => selectFilter(filter)"
                            >
                                {{ $t(`search.${filter}`) }}
                            </button>

                            <DSDropdown ref="sortDropdown">
                                <template #button>
                                    <button
                                        class="ds-btn ds-btn--tertiary sort-button"
                                        :class="{'is-open': sortButtonIsOpen}"
                                        @click="sortButtonIsOpen = !sortButtonIsOpen"
                                    >
                                        <span>{{ $t(`sort.${computedSortTitle}`) }}</span>
                                        <DSIcon code="default-line/chevron-right" class="caret-down" size="l" />
                                    </button>
                                </template>
                                <template #default="{ close }">
                                    <button @click="setSorting('title', 'asc'); close()">
                                        {{ $t('sort.popularity') }}
                                    </button>
                                    <button @click="setSorting('createdAt', 'asc'); close()">
                                        {{ $t('sort.newestFirst') }}
                                    </button>
                                    <button @click="setSorting('createdAt', 'desc'); close()">
                                        {{ $t('sort.oldestFirst') }}
                                    </button>
                                </template>
                            </DSDropdown>
                        </div>
                        <span v-else-if="!searchLoading">{{ $t('search.noResultsFound') }}</span>
                    </div>
                    <div v-if="!totalResults && !searchLoading && hasQuickLinks" class="search-quicklinks">
                        <h3 class="h3">{{ $t('search.quicklinksHeading') }}</h3>
                        <ul v-if="layoutData.searchQuicklinks">
                            <li v-for="(quicklink, index) in layoutData.searchQuicklinks" :key="index">
                                <nuxt-link :to="`/${quicklink.url?.url}`">{{ quicklink.title }}</nuxt-link>
                            </li>
                        </ul>
                    </div>
                    <div v-if="searchLoading" class="loading-indicator">
                        <DSPreloader aria-label="loading search results" round />
                    </div>
                    <div v-else class="results">
                        <template v-if="$slots['result-item']">
                            <slot
                                v-for="(result) in searchResults"
                                :key="result.content.id"
                                name="result-item"
                                :result="result"
                                :prompt="searchPrompt"
                            />
                        </template>
                        <template v-else>
                            <LayoutSearchResult
                                v-for="(result) in searchResults"
                                :key="result.content.id"
                                :result="result"
                                :prompt="searchPrompt"
                            />
                        </template>
                    </div>
                    <div v-if="searchPrompt && totalResults > 0" class="search-result-nav-buttons">
                        <button
                            class="ds-btn ds-btn--quartinary"
                            :disabled="paginatedPagesPage < 1"
                            @click="paginatedPagesPage = paginatedPagesPage-1"
                        >
                            <DSIcon code="st1website-fill/arrow-left" />
                        </button>
                        <button
                            v-if="paginatedPagesPage > 0"
                            class="ds-btn ds-btn--quartinary"
                            @click="loadFirstPage()"
                        >
                            1
                        </button>
                        <button v-if="paginatedPagesPage > 0" class="ds-btn ds-btn--quartinary">...</button>
                        <button
                            v-for="(page, index) in paginatedPages[paginatedPagesPage]"
                            :key="index"
                            class="ds-btn ds-btn--quartinary"
                            :class="{selected: page === currentPage }"
                            @click="loadPage(page)"
                        >
                            {{ page }}
                        </button>
                        <button
                            v-if="paginatedPages[paginatedPagesPage].length === paginatedPagesPerPage"
                            class="ds-btn ds-btn--quartinary"
                        >
                            ...
                        </button>
                        <button
                            v-if="paginatedPages[paginatedPagesPage].length === paginatedPagesPerPage"
                            class="ds-btn ds-btn--quartinary"
                            @click="loadLastPage()"
                        >
                            {{ pages }}
                        </button>
                        <button
                            class="ds-btn ds-btn--quartinary"
                            :disabled="paginatedPagesPage === (paginatedPages.length - 1)"
                            @click="paginatedPagesPage = paginatedPagesPage + 1"
                        >
                            <DSIcon code="st1website-fill/arrow-right" />
                        </button>
                    </div>
                </div>
            </div>
        </Teleport>
        <LayoutKeyboardEvents @keydown="keyboardEvent" />
    </div>
</template>
<script setup>
import {
    ref,
    inject,
    useNuxtApp,
    useCmsProxy,
    watch,
    useRoute,
    useI18n,
    computed,
    storeToRefs,
    useAnalytics,
    useRouteAnnouncer,
} from '#imports';
import { useModalStore } from '~dsc/stores/modal';
import { useLayoutStore } from '~dsc/stores/cms/layout';

const { gtm } = useAnalytics();
const modal = useModalStore();
const layout = inject('layout', { theme: 'white' });
const sortButtonIsOpen = ref(false);
const route = useRoute();
const { $paths } = useNuxtApp();
const paginatedPagesPage = ref(0);
const paginatedPagesPerPage = ref(10);

const { layoutData } = await storeToRefs(useLayoutStore());

const routeAnnouncer = useRouteAnnouncer();

const debounceTimeout = ref(null);

const searchPrompt = ref('');
const searchLoading = ref(false);
const searchResults = ref([]);
const totalResults = ref(0);
const currentPage = ref(1);

const currentlySelectedFilter = ref(null);
const currentFilterCodeToSearchCategory = computed(() => {
    switch (currentlySelectedFilter.value) {
        case 'stationPageTemplate':
        case 'helmisimpukkaStationPageTemplate':
            return 'Station';
        case 'articlePage':
            return 'Article';
        case 'standardPage':
        default:
            return 'Page';
    }
});
const filters = computed(() => [...new Set(searchResults.value.map(result => result.content.contentType))]);
const selectFilter = (filter = null) => {
    currentlySelectedFilter.value = filter;
    currentPage.value = 1;
    paginatedPagesPage.value = 0;
    search();
};
const clearSelectedFilter = () => selectFilter(null);
const sortBy = ref('title');
const sortDirection = ref('asc');

const searchResultsLimit = 5;

const pages = computed(() => Math.ceil(totalResults.value / searchResultsLimit));

const paginatedPages = computed(() => {
    if (!totalResults.value) return null;

    const pages = Math.ceil(totalResults.value / searchResultsLimit);
    const numbers = [];
    const chunked = [];

    for (let i = 1; i <= pages; i++) {
        numbers.push(i);
    }

    for (let i = 0; i < numbers.length; i += paginatedPagesPerPage.value) {
        chunked.push(numbers.slice(i, i + paginatedPagesPerPage.value));
    }

    return chunked;

});

const logo = computed(() => {
    if (!layoutData.logo) return { src: '/images/logo.svg', alt: 'logoAlt' };
    const { 0: logo } = layoutData.logo;

    return logo;
});

const hasQuickLinks = computed(() => (layoutData?.searchQuicklinks ?? []).length > 0);

const computedSortTitle = computed(() => {
    if (sortBy.value === 'createdAt') return sortDirection.value === 'asc' ? 'newestFirst' : 'oldestFirst';

    return 'popularity';
});

watch(() => route.path, () => {
    searchResults.value = [];
    searchPrompt.value = '';
    modal.unsetNameAndClose();
});

const clearSearchPrompt = () => {
    searchResults.value = [];
    searchPrompt.value = '';
};

const onSearchInput = () => {
    clearSelectedFilter();
};

const search = () => {
    if (debounceTimeout.value) {
        clearTimeout(debounceTimeout.value);
    }

    debounceTimeout.value = setTimeout(async () => {
        const MIN_PROMPT_LENGTH = 3;

        if (searchPrompt.value.length < MIN_PROMPT_LENGTH) {
            searchResults.value = [];
            currentPage.value = 1;

            return;
        }
        searchLoading.value = true;

        const body = {
            textQuery: searchPrompt.value,
            ...(currentlySelectedFilter.value ? { filter: { category: currentFilterCodeToSearchCategory.value } } : {}),
            page: currentPage.value,
            limit: searchResultsLimit,
            sort: {
                sortBy: sortBy.value,
                sortDirection: sortDirection.value,
            },
        };

        try {
            const searchResultsResponse = await useCmsProxy($paths.getEndpoint('search'), { method: 'POST', body });
            totalResults.value = searchResultsResponse?.total ?? 0;
            currentPage.value = searchResultsResponse?.page ?? 1;
            searchResults.value = searchResultsResponse?.results ?? [];
            searchLoading.value = false;
            if (totalResults.value < 1) {
                gtmPushNoResults();
            } else {
                gtmPushSearchResults();
            }
            routeAnnouncer.set(t('search.searchResultsExplanation', {
                searchPrompt: searchPrompt.value,
                total: totalResults.value,
                itemsPerPage: searchResultsLimit,
                page: currentPage.value,
                pages: pages.value,
            }));
        } catch (error) {
            console.error(error);
            searchLoading.value = false;
        } finally {
            searchLoading.value = false;
        }
        searchLoading.value = false;
    }, 500);
};

const activateSearch = () => {
    modal.setNameAndOpen('search');
    if (layout.value?.sidebars?.right) layout.value.sidebars.right = false;
};


const setSorting = (getSortBy, getSortDirection) => {
    sortBy.value = getSortBy;
    sortDirection.value = getSortDirection;
    search();
};

const loadPage = page => {
    currentPage.value = page;
    search(searchPrompt.value);
};

const loadFirstPage = () => {
    currentPage.value = 1;
    paginatedPagesPage.value = 0;
    search(searchPrompt.value);
};

const loadLastPage = () => {
    currentPage.value = pages.value;
    paginatedPagesPage.value = paginatedPages.value.length - 1;
    search(searchPrompt.value);
};

const gtmPushSearchResults = () => gtm().trackEvent({
    event: 'search',
    search_term: searchPrompt.value,
    search_category: currentFilterCodeToSearchCategory.value,
});

const gtmPushNoResults = () => gtm().trackEvent({
    event: 'struggle',
    search_term: searchPrompt.value,
    search_category: currentFilterCodeToSearchCategory.value,
    gtm: {
        error_text: 'search with zero results',
        fatal: false,
        error_status: 404,
        error_code: 'failed.search',
        error_type: 'struggle',
    },
});

const keyboardEvent = e => {
    switch (e.which) {
        case 191:
            modal.setNameAndOpen('search');
            break;

        default:
            break;
    }

};

const { t } = useI18n({
    useScope: 'global',
    messages: {
        'fi-FI': {
            search: {
                search: 'Haku',
                label: 'Hae sivustolta',
                searchResultsExplanation: 'Hakusanalla { searchPrompt } löytyi { total } tulosta. Näytetään { itemsPerPage } tulosta per sivu. Sivu { page }/{ pages }',
                searchResultsAmount: 'Hakutulokset',
                searchResultsPages: 'Sivu',
                popularity: 'Suosituin ensin',
                noResultsFound: '- Ei hakutuloksia',
                quicklinksHeading: 'Pikalinkit',
                showAll: 'Näytä kaikki',
                products: 'Tuotteet',
                standardPage: 'Sivu',
                stationPageTemplate: 'Asemat',
                helmisimpukkaStationPageTemplate: 'Asemat',
                articlePage: 'Artikkelit',
            },
            sort: {
                popularity: 'Suosituin ensin',
                newestFirst: 'Uusin ensin',
                oldestFirst: 'Vanhin ensin',
            },
        },
        'en-EU': {
            search: {
                search: 'Search',
                label: 'Search',
                searchResultsExplanation: 'Hakusanalla { searchPrompt } löytyi { total } tulosta. Näytetään { itemsPerPage } tulosta per sivu. Sivu { page }/{ pages }',
                searchResultsAmount: 'Search results',
                searchResultsPages: 'Page',
                popularity: 'Popularity',
                noResultsFound: '- No search results, please try a different search term or use the quicklinks below',
                quicklinksHeading: 'Quicklinks',
                showAll: 'Show all',
                products: 'Products',
                standardPage: 'Pages',
                stationPageTemplate: 'Stations',
                helmisimpukkaStationPageTemplate: 'Stations',
                articlePage: 'Articles',
            },
            sort: {
                popularity: 'Popularity',
                newestFirst: 'Newest First',
                oldestFirst: 'Oldest First',
            },
        },
        'sv-SE': {
            search: {
                search: 'Sök',
                label: 'Sök',
                searchResultsExplanation: 'Sökresultat',
                searchResultsAmount: 'Sökresultat',
                searchResultsPages: 'sida',
                popularity: 'Populäritet',
                noResultsFound: '- Inga resultat hittades, vänligen försök ändra din sökning eller använd snabblänkarna nedan',
                quicklinksHeading: 'Snabblänkar',
                showAll: 'Visa all',
                products: 'Produkter',
                standardPage: 'Sidor',
                stationPageTemplate: 'Stationer',
                helmisimpukkaStationPageTemplate: 'Stationer',
                articlePage: 'Artiklar',
            },
            sort: {
                popularity: 'Populäritet',
                newestFirst: 'Nyast först',
                oldestFirst: 'Äldst först',
            },
        },
        'no-NO': {
            search: {
                search: 'Søk',
                label: 'Søk',
                searchResultsExplanation: 'Søkeresultat',
                searchResultsAmount: 'Søkeresultat',
                searchResultsPages: 'side',
                popularity: 'Populæritet',
                noResultsFound: '- Ingen resultat funnet',
                quicklinksHeading: 'Hurtigkoblinger',
                showAll: 'Vis alle',
                products: 'Produkter',
                standardPage: 'Sider',
                stationPageTemplate: 'Stasjoner',
                helmisimpukkaStationPageTemplate: 'Stasjoner',
                articlePage: 'Artikler',
            },
            sort: {
                popularity: 'Populæritet',
                newestFirst: 'Nyest først',
                oldestFirst: 'Eldst først',
            },
        },
    },
});

</script>
<style scoped>
.search {
  position: relative;
}
.search.theme-clear {
  color: var(--color-base-white);
}
.search.theme-clear .ds-btn {
  background: transparent;
  border-color: transparent;
  color: inherit;
}
@media (width >= 1439px) {
  .search.theme-clear .ds-btn:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: transparent;
  }
}
.search.theme-white {
  background: transparent;
}
.search.theme-white .ds-btn {
  border-color: transparent;
}
.search.theme-white .ds-btn:hover {
  background: var(--color-base-grey-100);
  border-color: var(--color-base-grey-100);
}
@media (width <= 1439px) {
  .search.theme-white, .search.theme-clear {
    color: var(--color-base-black);
  }
  .search.theme-white .ds-btn, .search.theme-clear .ds-btn {
    padding: 1rem;
    border: 0.125rem solid var(--color-secondary-150);
    border-radius: 0.5rem;
    width: 100%;
    justify-content: space-between;
    font-weight: 400;
  }
}
.search button.ds-btn {
  border-width: 2px;
  border-style: solid;
  align-items: center;
}
.search button.ds-btn span {
  display: grid;
  align-items: center;
}
.search.theme-black .label {
  color: var(--color-base-white);
}
.search.theme-black .option {
  color: var(--color-base-white);
}
.search.theme-black .option.selected {
  color: var(--color-secondary-500);
  border-bottom: 0.25rem solid var(--color-secondary-500);
}
.search .search-button {
  display: inline-flex;
  align-items: center;
}
.search .search-button .icon {
  margin-left: 0.5rem;
}
.search .label {
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  text-transform: uppercase;
  padding: 0;
  min-height: 1.5rem;
  min-width: 35px;
  justify-content: flex-end;
  font-weight: 400;
}
.search .label .icon {
  transition: all 0.2s ease-in-out;
  position: relative;
  max-width: 0.5rem;
  margin-left: 0.25rem;
  transform: rotate(0) translateY(0);
  transform-origin: center center;
}
.search .label:focus-visible {
  outline: -webkit-focus-ring-color auto 1px;
}

.head {
  align-items: center;
}

.ds-modal-search .ds-input .icon {
  margin-top: 0.5rem;
}
.ds-modal-search .body {
  overflow-x: hidden;
  padding-bottom: 0;
}
.ds-modal-search input:focus + .label {
  position: relative;
  top: -1rem;
  font-size: 0.5rem;
  line-height: 0.5rem;
}
.ds-modal-search input.has-text + .label {
  position: relative;
  top: -1rem;
  font-size: 0.5rem;
  line-height: 0.5rem;
}

.search-results-header {
  display: flex;
  justify-content: space-between;
  gap: 4rem;
  margin-top: 1rem;
}
@media (width <= 1439px) {
  .search-results-header {
    gap: 2rem;
    flex-direction: column;
  }
}
.search-results-header .results-summary {
  display: flex;
  gap: 4rem;
}
@media (width <= 1439px) {
  .search-results-header .results-summary {
    border-top: 1px solid var(--color-base-grey-300);
    border-bottom: 1px solid var(--color-base-grey-300);
    padding: 1rem 0;
    justify-content: space-between;
  }
}
.search-results-header .results-summary .result-data {
  font-weight: 700;
}
.search-results-header .results-options {
  display: flex;
  gap: 1rem;
  white-space: nowrap;
  flex-wrap: wrap;
}
.search-results-header .results-options button.selected {
  background-color: var(--color-base-grey-200);
}
.search-results-header .results-options button:hover {
  background-color: var(--color-base-grey-200);
}
.search-results-header .results-options :deep(.ds-dropdown) .menu {
  right: 0.25rem;
}
.search-results-header .results-options button.sort-button {
  background-color: var(--color-base-white);
  border: 2px solid var(--color-primary-500);
}
.search-results-header .results-options button.sort-button .icon {
  transform: rotate(90deg);
}
.search-results-header .results-options button.sort-button.is-open .icon {
  transform: rotate(-90deg);
}

.search-quicklinks {
  padding: 2rem 0;
}
.search-quicklinks .h3 {
  font-family: "Nunito Sans", sans-serif;
  font-weight: 600;
}
.search-quicklinks ul {
  list-style: none;
  margin-left: 0;
  font-size: 1.25rem;
  line-height: 2.5rem;
}
.search-quicklinks ul li a {
  color: var(--color-base-grey-500);
}

.results {
  display: flex;
  flex-direction: column;
  margin: 1rem 0;
  gap: 0.25rem 0;
}

.search-result-nav-buttons {
  display: flex;
  justify-content: space-around;
}
.search-result-nav-buttons button {
  bottom: 0.25rem;
}
.search-result-nav-buttons button:disabled {
  background-color: var(--color-base-white);
}
.search-result-nav-buttons button.selected {
  background-color: var(--color-base-grey-200);
}
</style>
